import React from 'react';
import { Link } from 'react-router-dom';
import {
  formatDay,
  formatGuideRange,
  formatInt,
  formatMoneyCompact,
  formatPct,
  formatPropertyType,
  formatSaleMethod,
} from '../format';
import { pagePath } from '../siteConfig';
import { track } from '../../utils/track';
import GapChip from './GapChip';

function AgentCell({ agent, from }) {
  if (!agent) return <span className="re-muted">{'—'}</span>;
  const label = (
    <>
      <span className="re-cell__primary">{agent.name}</span>
      {agent.agency ? <span className="re-cell__secondary">{agent.agency}</span> : null}
    </>
  );
  if (!agent.slug) return <span className="re-cell">{label}</span>;
  return (
    <Link
      className="re-cell re-cell--link"
      to={pagePath('agent', agent.slug)}
      onClick={() => track('agent_link_click', { from, agent_slug: agent.slug })}
    >
      {label}
    </Link>
  );
}

/** Recent sales with guide and result side by side. */
export function ResultsTable({ results, showAgent = true, showSuburb = false, caption, from }) {
  if (!results?.length) return <p className="re-empty">No recent sales with a published guide.</p>;
  return (
    <div className="re-table-wrap" role="region" aria-label={caption} tabIndex={0}>
      <table className="re-table re-table--results">
        <caption className="re-sr-only">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Property</th>
            <th scope="col">Sold</th>
            <th scope="col" className="is-num">Guide</th>
            <th scope="col" className="is-num">Result</th>
            <th scope="col" className="is-num">Gap</th>
            {showAgent ? <th scope="col">Agent</th> : null}
          </tr>
        </thead>
        <tbody>
          {results.map((r, i) => (
            <tr key={`${r.address}-${r.sold_date}-${i}`}>
              <th scope="row">
                <span className="re-cell__primary">{r.address}</span>
                <span className="re-cell__secondary">
                  {formatPropertyType(r.property_type, r.bedrooms)}
                  {showSuburb && r.suburb ? (
                    <>
                      {' · '}
                      <Link to={pagePath('suburb', r.suburb.slug)}>{r.suburb.name}</Link>
                    </>
                  ) : null}
                </span>
              </th>
              <td>
                <span className="re-cell__primary">{formatDay(r.sold_date)}</span>
                <span className="re-cell__secondary">{formatSaleMethod(r.sale_method)}</span>
              </td>
              <td className="is-num">
                {r.guide_low === null && r.guide_high === null ? (
                  <>
                    <span className="re-cell__primary">{formatMoneyCompact(r.guide_midpoint)}</span>
                    <span className="re-cell__secondary">Guide midpoint</span>
                  </>
                ) : formatGuideRange(r.guide_low, r.guide_high)}
              </td>
              <td className="is-num re-strong">{formatMoneyCompact(r.sale_price)}</td>
              <td className="is-num">
                <GapChip value={r.guide_gap_pct} />
              </td>
              {showAgent ? (
                <td>
                  <AgentCell agent={r.agent} from={from} />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Agents ranked by typical distance from guide (smallest first). */
export function AgentRankList({ agents, caption, from }) {
  if (!agents?.length) return null;
  const sorted = [...agents].sort((a, b) => (a.guide_error_abs_pct ?? 1e9) - (b.guide_error_abs_pct ?? 1e9));
  // Peer lists carry fewer fields than suburb agent lists; drop empty columns.
  const showGap = agents.some((a) => a.guide_gap_pct !== undefined);
  const showDays = agents.some((a) => a.median_days_on_market !== undefined);
  return (
    <div className="re-table-wrap" role="region" aria-label={caption} tabIndex={0}>
      <table className="re-table re-table--agents">
        <caption className="re-sr-only">{caption}</caption>
        <thead>
          <tr>
            <th scope="col" className="is-rank">#</th>
            <th scope="col">Agent</th>
            <th scope="col" className="is-num">Typical miss</th>
            {showGap ? <th scope="col" className="is-num">Avg gap</th> : null}
            <th scope="col" className="is-num">Sales</th>
            {showDays ? <th scope="col" className="is-num re-hide-sm">Median days</th> : null}
          </tr>
        </thead>
        <tbody>
          {sorted.map((a, i) => (
            <tr key={a.slug || a.name}>
              <td className="is-rank">{i + 1}</td>
              <th scope="row">
                <AgentCell agent={a.has_page ? a : { ...a, slug: null }} from={from} />
              </th>
              <td className="is-num re-strong">{formatPct(a.guide_error_abs_pct)}</td>
              {showGap ? (
                <td className="is-num">
                  <GapChip value={a.guide_gap_pct} />
                </td>
              ) : null}
              <td className="is-num">{formatInt(a.guide_matched_sales)}</td>
              {showDays ? <td className="is-num re-hide-sm">{formatInt(a.median_days_on_market)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Where an agent sells, against each suburb's overall gap. */
export function AgentSuburbTable({ suburbs, agentName }) {
  if (!suburbs?.length) return null;
  return (
    <div className="re-table-wrap" role="region" aria-label={`Suburbs where ${agentName} sells`} tabIndex={0}>
      <table className="re-table re-table--suburbs">
        <caption className="re-sr-only">{`Suburbs where ${agentName} sells`}</caption>
        <thead>
          <tr>
            <th scope="col">Suburb</th>
            <th scope="col" className="is-num">Sales</th>
            <th scope="col" className="is-num">Their avg gap</th>
            <th scope="col" className="is-num">Suburb gap</th>
          </tr>
        </thead>
        <tbody>
          {suburbs.map((s) => (
            <tr key={s.slug}>
              <th scope="row">
                {s.has_page ? (
                  <Link className="re-cell re-cell--link" to={pagePath('suburb', s.slug)}>
                    <span className="re-cell__primary">{s.name}</span>
                  </Link>
                ) : (
                  <span className="re-cell__primary">{s.name}</span>
                )}
              </th>
              <td className="is-num">{formatInt(s.guide_matched_sales)}</td>
              <td className="is-num">
                <GapChip value={s.guide_gap_pct} />
              </td>
              <td className="is-num">
                <GapChip value={s.suburb_guide_gap_pct} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
